import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Upload, FileDown, AlertCircle, CheckCircle2 } from "lucide-react";

const BLANK_Q = {
  subject_id: "", topic_id: "", text: "",
  options: { A: "", B: "", C: "", D: "" },
  correct_option: "A", explanation: "",
  option_explanations: { A: "", B: "", C: "", D: "" },
  difficulty: "Medium", image_url: "", reference: "", tags: [], status: "published",
};

export default function QuestionsPanel() {
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK_Q);
  const [includeD, setIncludeD] = useState(false);
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [formChapters, setFormChapters] = useState([]);
  const fileRef = useRef();

  useEffect(() => { api.get("/subjects").then((r) => setSubjects(r.data)); }, []);

  const loadChaptersFor = async (sid) => {
    if (!sid) return [];
    const { data } = await api.get(`/topics?subject_id=${sid}`);
    return data;
  };
  useEffect(() => {
    if (subjectId) loadChaptersFor(subjectId).then(setChapters);
    else setChapters([]);
    setTopicFilter("");
  }, [subjectId]);

  const load = () => {
    const parts = [];
    if (subjectId) parts.push(`subject_id=${subjectId}`);
    if (topicFilter) parts.push(`topic_id=${topicFilter}`);
    parts.push("limit=200");
    api.get(`/questions?${parts.join("&")}`).then((r) => setItems(r.data.items));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [subjectId, topicFilter]);

  useEffect(() => {
    if (form.subject_id) loadChaptersFor(form.subject_id).then(setFormChapters);
    else setFormChapters([]);
  }, [form.subject_id]);

  const openCreate = () => { setEditing(null); setForm({ ...BLANK_Q, subject_id: subjectId || subjects[0]?.id || "" }); setIncludeD(false); setOpen(true); };
  const openEdit = (q) => {
    setEditing(q);
    const opts = q.options || {};
    setForm({
      subject_id: q.subject_id, topic_id: q.topic_id || "",
      text: q.text, options: { A: opts.A || "", B: opts.B || "", C: opts.C || "", D: opts.D || "" },
      correct_option: q.correct_option,
      explanation: q.explanation || "",
      option_explanations: q.option_explanations || { A: "", B: "", C: "", D: "" },
      difficulty: q.difficulty, image_url: q.image_url || "",
      reference: q.reference || "", tags: q.tags || [], status: "published",
    });
    setIncludeD(!!opts.D);
    setOpen(true);
  };

  const save = async () => {
    try {
      const options = { A: form.options.A, B: form.options.B, C: form.options.C };
      if (includeD && form.options.D) options.D = form.options.D;
      const payload = {
        ...form,
        options,
        topic_id: form.topic_id || null,
        image_url: form.image_url || null,
        reference: form.reference || null,
      };
      if (!includeD && payload.correct_option === "D") {
        toast.error("Correct answer is D but Option D is not enabled");
        return;
      }
      if (editing) await api.put(`/questions/${editing.id}`, payload);
      else await api.post("/questions", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!confirm("Delete this question? Student history is preserved.")) return;
    await api.delete(`/questions/${id}`);
    toast.success("Deleted"); load();
  };

  const chooseFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/questions/preview-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setPendingFile(f); setPreviewOpen(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    e.target.value = "";
  };

  const confirmImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", pendingFile);
    try {
      const { data } = await api.post("/questions/import-csv?skip_duplicates=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} · Skipped ${data.skipped_duplicates} duplicates · ${data.errors.length} errors`);
      setPreviewOpen(false); setPreview(null); setPendingFile(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setImporting(false); }
  };

  const downloadTemplate = () => {
    const csv = "Subject,Chapter,Question,Option A,Option B,Option C,Option D,Correct Option,Explanation,Difficulty,Image URL,Reference\nAIR_NAV,Dead Reckoning,Sample 3-option question?,Opt A,Opt B,Opt C,,A,Explain it,Easy,,ICAO\nAIR_NAV,Chart Projections,Sample 4-option question?,Opt A,Opt B,Opt C,Opt D,D,Explain it,Medium,,ICAO\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "pilotyatra_questions_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={subjectId || "all"} onValueChange={(v) => setSubjectId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="filter-subject-select"><SelectValue placeholder="All subjects" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All subjects</SelectItem>
            {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={topicFilter || "all"} onValueChange={(v) => setTopicFilter(v === "all" ? "" : v)} disabled={!subjectId}>
          <SelectTrigger className="w-56" data-testid="filter-chapter-select"><SelectValue placeholder="All chapters" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All chapters</SelectItem>
            {chapters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={openCreate} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="new-question-btn">
          <Plus className="mr-2 h-4 w-4" /> Add question
        </Button>
        <input type="file" accept=".csv" hidden ref={fileRef} onChange={chooseFile} />
        <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-csv-btn">
          <Upload className="mr-2 h-4 w-4" /> Import CSV
        </Button>
        <Button variant="ghost" onClick={downloadTemplate} data-testid="download-template-btn">
          <FileDown className="mr-2 h-4 w-4" /> Download template
        </Button>
      </div>
      <Card className="border-border/70">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Subject</TableHead><TableHead>Chapter</TableHead><TableHead>Question</TableHead><TableHead>Correct</TableHead><TableHead>Difficulty</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((q) => (
                <TableRow key={q.id}>
                  <TableCell className="text-xs text-muted-foreground">{q.subject_name}</TableCell>
                  <TableCell className="text-xs">{q.topic_name || "—"}</TableCell>
                  <TableCell className="max-w-md truncate">{q.text}</TableCell>
                  <TableCell className="font-mono text-xs">{q.correct_option}</TableCell>
                  <TableCell className="text-xs">{q.difficulty}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(q)} data-testid={`edit-q-${q.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(q.id)} data-testid={`del-q-${q.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="p-10 text-center text-sm text-muted-foreground">No questions match these filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit / Create question dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit question" : "New question"}</DialogTitle>
            <DialogDescription>At least 3 options (A/B/C) are required. Option D is optional. The correct answer stays on the server.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <Label>Subject</Label>
                <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v, topic_id: "" })}>
                  <SelectTrigger data-testid="qform-subject"><SelectValue placeholder="Choose" /></SelectTrigger>
                  <SelectContent>
                    {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Chapter</Label>
                <Select value={form.topic_id || "none"} onValueChange={(v) => setForm({ ...form, topic_id: v === "none" ? "" : v })} disabled={formChapters.length === 0}>
                  <SelectTrigger data-testid="qform-chapter"><SelectValue placeholder={formChapters.length ? "Choose chapter" : "Add chapters in Subjects tab first"} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {formChapters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <Label>Difficulty</Label>
                <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                  <SelectTrigger data-testid="qform-difficulty"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Easy">Easy</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Correct option</Label>
                <Select value={form.correct_option} onValueChange={(v) => setForm({ ...form, correct_option: v })}>
                  <SelectTrigger data-testid="qform-correct"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">A</SelectItem>
                    <SelectItem value="B">B</SelectItem>
                    <SelectItem value="C">C</SelectItem>
                    {includeD && <SelectItem value="D">D</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Question text</Label>
              <Textarea rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="qform-text" />
            </div>
            {["A", "B", "C"].map((k) => (
              <div key={k} className="grid gap-2 sm:grid-cols-[70px_1fr] sm:items-center">
                <Label>Option {k}</Label>
                <Input value={form.options[k]} onChange={(e) => setForm({ ...form, options: { ...form.options, [k]: e.target.value } })} data-testid={`qform-opt-${k}`} />
              </div>
            ))}
            <div className="grid gap-2 sm:grid-cols-[70px_1fr] sm:items-center">
              <Label className="flex items-center gap-2">
                <input type="checkbox" checked={includeD} onChange={(e) => setIncludeD(e.target.checked)} data-testid="qform-include-d" />
                Option D
              </Label>
              <Input value={form.options.D} onChange={(e) => setForm({ ...form, options: { ...form.options, D: e.target.value } })} placeholder={includeD ? "" : "Enable Option D to add a 4th option"} disabled={!includeD} data-testid="qform-opt-D" />
            </div>
            <div>
              <Label>Explanation</Label>
              <Textarea rows={3} value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} data-testid="qform-explanation" />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <Label>Image URL</Label>
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="qform-image" />
              </div>
              <div>
                <Label>Reference</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="qform-reference" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="qform-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV preview dialog */}
      <Dialog open={previewOpen} onOpenChange={(v) => { setPreviewOpen(v); if (!v) { setPreview(null); setPendingFile(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview import</DialogTitle>
            <DialogDescription>Review the file before saving. Duplicates will be skipped automatically.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="rounded-lg border border-border bg-emerald-50 p-3 text-center">
                  <div className="font-display text-2xl font-bold text-emerald-700">{preview.valid}</div>
                  <div className="text-xs uppercase text-emerald-800">Valid rows</div>
                </div>
                <div className="rounded-lg border border-border bg-amber-50 p-3 text-center">
                  <div className="font-display text-2xl font-bold text-amber-700">{preview.db_duplicate_count + preview.in_file_duplicates_rows.length}</div>
                  <div className="text-xs uppercase text-amber-800">Duplicates (skip)</div>
                </div>
                <div className="rounded-lg border border-border bg-rose-50 p-3 text-center">
                  <div className="font-display text-2xl font-bold text-rose-700">{preview.errors.length}</div>
                  <div className="text-xs uppercase text-rose-800">Errors</div>
                </div>
                <div className="rounded-lg border border-border bg-secondary/50 p-3 text-center">
                  <div className="font-display text-2xl font-bold">{preview.total_rows}</div>
                  <div className="text-xs uppercase text-muted-foreground">Total rows</div>
                </div>
              </div>

              {preview.errors.length > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-700">
                    <AlertCircle className="h-4 w-4" /> Errors — these rows will be skipped
                  </div>
                  <ul className="max-h-32 overflow-y-auto text-xs text-rose-900">
                    {preview.errors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                </div>
              )}

              {preview.sample.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" /> First rows preview
                  </div>
                  <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>#</TableHead><TableHead>Subject</TableHead><TableHead>Chapter</TableHead><TableHead>Question</TableHead><TableHead>Correct</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {preview.sample.map((r) => (
                          <TableRow key={r.row}>
                            <TableCell className="font-mono text-xs">{r.row}</TableCell>
                            <TableCell className="text-xs">{r.subject}</TableCell>
                            <TableCell className="text-xs">{r.chapter || "—"}</TableCell>
                            <TableCell className="max-w-xs truncate text-xs">{r.text}</TableCell>
                            <TableCell className="font-mono text-xs">{r.correct}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Cancel</Button>
            <Button onClick={confirmImport} disabled={importing || !preview || preview.valid === 0} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="confirm-import-btn">
              {importing ? "Importing…" : `Import ${preview?.valid || 0} questions`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
